import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const reviews = [
  {
    name: 'Glenn Vargas',
    badge: 'Local Guide',
    timeAgo: '34 weeks ago',
    rating: 5,
    text: 'Micah is a life long learner. I met Micah at a dog training seminar where she was furthering her knowledge from one of the best trainers in the training world. Micah brought one of her rescue/foster dogs that she was graciously working with to rehab and find a new home. Micah got up and showcased the dog\'s ability which was nothing short of spectacular. The dog\'s ability was a direct reflection of Micah\'s expertise. I highly recommend her if you want a stronger relationship with your pup.',
  },
  {
    name: 'Spencer Palanuk',
    badge: '71 reviews',
    timeAgo: '',
    rating: 5,
    text: 'I worked with Farm Dog Oklahoma at a seminar and received helpful advice about leash work. Would recommend!',
  },
];

const GoogleReviewsCarousel = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (reviews.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % reviews.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  const goTo = (index) => setCurrent(index);
  const prev = () => setCurrent((c) => (c - 1 + reviews.length) % reviews.length);
  const next = () => setCurrent((c) => (c + 1) % reviews.length);

  return (
    <section className="py-12 bg-white" data-testid="google-reviews-section">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <svg viewBox="0 0 24 24" className="w-7 h-7" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          <h3 className="text-2xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Google Reviews
          </h3>
        </div>

        {/* Carousel */}
        <div className="max-w-3xl mx-auto relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.35 }}
              className="bg-[#F5F5F0] rounded-2xl p-8 shadow-md"
              data-testid="review-card"
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4 justify-center">
                {Array.from({ length: reviews[current].rating }).map((_, i) => (
                  <Star key={i} size={22} className="fill-[#FBBC05] text-[#FBBC05]" />
                ))}
              </div>

              {/* Review text */}
              <p className="text-gray-700 leading-relaxed text-center text-lg italic mb-6">
                "{reviews[current].text}"
              </p>

              {/* Reviewer info */}
              <div className="flex items-center justify-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#C4A052] flex items-center justify-center text-white font-bold text-lg">
                  {reviews[current].name.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-[#5C4033]">{reviews[current].name}</p>
                  <p className="text-xs text-gray-500">
                    {reviews[current].badge} &middot; {reviews[current].timeAgo}
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation arrows (only if more than 1 review) */}
          {reviews.length > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-[#5C4033] hover:bg-[#F5F5F0] transition-colors"
                aria-label="Previous review"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={next}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-[#5C4033] hover:bg-[#F5F5F0] transition-colors"
                aria-label="Next review"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}
        </div>

        {/* Dots (only if more than 1 review) */}
        {reviews.length > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {reviews.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${i === current ? 'bg-[#C4A052]' : 'bg-gray-300'}`}
                aria-label={`Go to review ${i + 1}`}
              />
            ))}
          </div>
        )}

        {/* See all on Google link */}
        <div className="text-center mt-6">
          <a
            href="https://share.google/pxkh5HDmlOsYwKgsh"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-[#C4A052] hover:text-[#B8935E] font-semibold transition-colors"
            data-testid="see-all-reviews-link"
          >
            See all reviews on Google
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M5.22 14.78a.75.75 0 0 0 1.06 0l7.22-7.22v5.69a.75.75 0 0 0 1.5 0v-7.5a.75.75 0 0 0-.75-.75h-7.5a.75.75 0 0 0 0 1.5h5.69l-7.22 7.22a.75.75 0 0 0 0 1.06z" clipRule="evenodd"/></svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsCarousel;
